import React, { useEffect, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';

/* ============================================================
   BetaWelcome — one-time greeting for every new account.
   FitFlex is in beta; early users should FEEL early: valued,
   heard, and warned (kindly) that things will keep evolving.
   Shows once per user (localStorage flag), before the product
   tour — dismissing it raises an event TourGate listens for.
============================================================ */

// Where "Share feedback" goes — update when a dedicated address exists.
const FEEDBACK_EMAIL = 'feedback@fitflex.club';

export const betaKey = (userId: string) => `fitflex_beta_welcome:${userId}`;
export const BETA_DISMISSED_EVENT = 'fitflex:beta-dismissed';

const BetaWelcome: React.FC = () => {
  const { user, dataLoaded, tourMode } = useFitStore();
  const [dismissed, setDismissed] = useState(false);

  // Re-arm for a different account in the same browser.
  useEffect(() => setDismissed(false), [user?.id]);

  if (!user || !dataLoaded || tourMode || dismissed) return null;
  if (localStorage.getItem(betaKey(user.id))) return null;

  const close = () => {
    localStorage.setItem(betaKey(user.id), '1');
    setDismissed(true);
    window.dispatchEvent(new Event(BETA_DISMISSED_EVENT));
  };

  const first = (user.name || '').split(' ')[0] || 'there';

  return (
    <div className="modal-overlay" style={{ zIndex: 10010 }} onClick={close}>
      <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="card flex flex-col gap-4" style={{ padding: 28 }}>
          <div>
            <p className="mono-label mb-3">Early access</p>
            <h2 className="text-[26px] font-bold tracking-tight leading-tight">
              You're in early, {first}.
            </h2>
          </div>
          <p className="text-[14px] text-fit-text-secondary leading-relaxed">
            FitFlex is in <span className="text-white font-semibold">beta</span> — you're one of the
            first inside. Things will improve fast, and a few things may shift while we build.
            That's the deal of being early: <span className="text-white font-semibold">your feedback
            steers what gets built next</span>, and we don't take your time for granted.
          </p>
          <p className="text-[13px] text-fit-text-secondary leading-relaxed">
            Spot something rough? Wish a feature existed? Tell us — every note gets read.
          </p>
          <div className="flex items-center gap-2 mt-1">
            <button onClick={close} className="btn btn-primary btn-sm flex-1">
              Let's go
            </button>
            <a
              href={`mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent('FitFlex beta feedback')}`}
              onClick={close}
              className="btn btn-glass btn-sm shrink-0"
            >
              ✉ Share feedback
            </a>
          </div>
          <p className="text-[11px] text-fit-muted text-center mt-1">
            By continuing you agree to our{' '}
            <a href="/terms" className="underline underline-offset-2 hover:text-white">Terms</a>{' '}and{' '}
            <a href="/privacy" className="underline underline-offset-2 hover:text-white">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BetaWelcome;
